/**
 * Environment Configuration and Feature Flags
 * Centralizes environment detection and feature flags for safe development/production separation
 */

// Environment Detection - Order matters for accuracy
const detectEnvironment = () => {
  // 1. Firebase Functions environment (has FIREBASE_CONFIG)
  if (typeof process !== 'undefined' && process.env.FIREBASE_CONFIG) {
    return 'firebase-functions';
  }
  
  // 2. React emulator mode (REACT_APP_USE_EMULATORS=true) - CHECK FIRST!
  if (typeof process !== 'undefined' && process.env.REACT_APP_USE_EMULATORS === 'true') {
    return 'emulator';
  }
  
  // 3. React development server (NODE_ENV=development) - but not emulator mode
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
    return 'development';
  }
  
  // 4. Browser production build
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'local-production';
  }
  
  // 5. Default to production
  return 'production';
};

export const ENVIRONMENT = detectEnvironment();

// Production Safety Check - NEVER skip verification in production
// Only use emulators if explicitly set to 'true' string AND we're on localhost
const IS_LOCALHOST = typeof window !== 'undefined' && window.location.hostname === 'localhost';
const USE_EMULATORS_ENV = typeof process !== 'undefined' && process.env.REACT_APP_USE_EMULATORS === 'true';
const FORCE_EMULATOR_MODE = IS_LOCALHOST && USE_EMULATORS_ENV;

// Feature Flags - Safe defaults that don't break production
export const FEATURES = {
  // Authentication & Security - CRITICAL: NEVER skip in production
  SKIP_EMAIL_VERIFICATION: FORCE_EMULATOR_MODE,
  MOCK_STRIPE_INTEGRATION: FORCE_EMULATOR_MODE,
  RELAXED_FIRESTORE_RULES: FORCE_EMULATOR_MODE,
  
  // Development Tools
  ENABLE_DEBUG_LOGS: FORCE_EMULATOR_MODE,
  ENABLE_CONSOLE_HELPERS: FORCE_EMULATOR_MODE,
  SHOW_DEBUG_INFO: FORCE_EMULATOR_MODE,
  
  // Firebase - CRITICAL: Only use emulators on localhost with flag
  USE_FIREBASE_EMULATORS: FORCE_EMULATOR_MODE,
  USE_PRODUCTION_FIREBASE: !FORCE_EMULATOR_MODE,
  
  // UI/UX
  SHOW_ENVIRONMENT_BANNER: FORCE_EMULATOR_MODE,
  ENABLE_PERFORMANCE_MONITORING: !FORCE_EMULATOR_MODE,
};

// Safe Console Logging - Only logs in development
export const devLog = {
  info: (...args) => {
    // Disabled in production for security
  },
  warn: (...args) => {
    // Disabled in production for security
  },
  error: (...args) => {
    // Disabled in production for security
  },
  success: (...args) => {
    // Disabled in production for security
  }
};

// Environment Info for Debugging
export const getEnvironmentInfo = () => ({
  environment: ENVIRONMENT,
  features: FEATURES,
  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
  hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
  nodeEnv: typeof process !== 'undefined' ? process.env.NODE_ENV : 'N/A',
  useEmulators: typeof process !== 'undefined' ? process.env.REACT_APP_USE_EMULATORS : 'N/A',
});

// Make environment info available in development console
if (FEATURES.ENABLE_CONSOLE_HELPERS && typeof window !== 'undefined') {
  window.envInfo = getEnvironmentInfo;
  window.features = FEATURES;
}